// ============================================
// フォーム
// ============================================

const REQUIRED_FIELD_NAMES = [
  'radio',
  'company',
  'full_name',
  'staff_name',
  'kana',
  'zip',
  'address',
  'email',
  'tel',
  'tool',
  'delivery_time',
  'message',
  'privacy[]',
];
const ZIP_FIELD_NAMES = ['zip'];
const TEL_FIELD_NAMES = ['tel', 'fax'];

// お問い合わせページ全体
function getContactPage() {
  return document.querySelector('[data-contact-page]');
}

// CF7 の form 本体
function getContactForm() {
  return getContactPage()?.querySelector('.wpcf7 form');
}

// name 属性でフォーム部品をまとめて取得
function getFields(form, name) {
  return Array.from(form.elements).filter((element) => element.name === name);
}

// 確認画面まわりで使う要素をまとめて取得
function getFormParts(form) {
  return {
    formWrap: form.querySelector('#form'),
    inputBlock: form.querySelector('[data-inputs]'),
    confirmBlock: form.querySelector('[data-confirm]'),
    confirmButton: form.querySelector('[data-to-confirm]'),
    sendWrap: form.querySelector('[data-to-send]'),
    backButton: form.querySelector('[data-back]'),
    defaultSubmit: form.querySelector('.wpcf7-submit'),
  };
}

// HTML をそのまま差し込まないように最低限のエスケープを行う
function escapeHTML(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// textarea の改行を確認画面でも見やすく保つ
function nl2br(value) {
  return escapeHTML(value).replace(/\r?\n/g, '<br>');
}

// ラジオ、チェックボックス、select などを考慮して表示用の値を返す
function getFieldValue(form, name) {
  const fields = getFields(form, name);

  if (fields.length === 0) {
    return '';
  }

  const [firstField] = fields;

  if (firstField.type === 'radio') {
    const checkedField = fields.find((field) => field.checked);
    return checkedField ? checkedField.value.trim() : '';
  }

  if (firstField.type === 'checkbox') {
    return fields
      .filter((field) => field.checked)
      .map((field) => field.value.trim())
      .join('、');
  }

  if (firstField.tagName === 'SELECT' && firstField.multiple) {
    return Array.from(firstField.selectedOptions)
      .map((option) => option.value.trim())
      .join('、');
  }

  return firstField.value.trim();
}

function toggleDisplay(element, isVisible, displayValue = '') {
  if (!element) {
    return;
  }

  element.style.display = isVisible ? displayValue : 'none';
}

// ステップ表示の見た目を切り替える
function setStep(step) {
  const flowItems = document.querySelectorAll('[data-contact-step]');

  flowItems.forEach((item) => {
    const isActive = item.dataset.contactStep === step;
    item.classList.toggle('is-active', isActive);

    if (isActive) {
      item.setAttribute('aria-current', 'step');
    } else {
      item.removeAttribute('aria-current');
    }
  });
}

function showInputStep(form) {
  const { formWrap, inputBlock, confirmBlock, confirmButton, sendWrap } = getFormParts(form);

  toggleDisplay(formWrap, true);
  toggleDisplay(inputBlock, true);
  toggleDisplay(confirmBlock, false);
  toggleDisplay(confirmButton, true, 'flex');
  toggleDisplay(sendWrap, false);
  setStep('input');
}

function showConfirmStep(form) {
  const { inputBlock, confirmBlock, confirmButton, sendWrap } = getFormParts(form);

  syncConfirmValues(form);
  toggleDisplay(inputBlock, false);
  toggleDisplay(confirmBlock, true);
  toggleDisplay(confirmButton, false);
  toggleDisplay(sendWrap, true);
  setStep('confirm');
}

function showCompleteStep(form) {
  const { formWrap } = getFormParts(form);

  toggleDisplay(formWrap, false);
  setStep('complete');
}

function scrollToContactTop() {
  // フォーム上部（フロー）を基準にする。上部に注文案内などがあってもページ最上部まで戻らないようにする
  const contactPage = getContactPage();
  const target = contactPage?.querySelector('[data-contact-flow]') ?? contactPage;

  if (!target) {
    return;
  }

  target.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
}

// 確認画面に今入力されている値を反映する
function syncConfirmValues(form) {
  const confirmFields = form.querySelectorAll('[data-field]');

  confirmFields.forEach((field) => {
    const value = getFieldValue(form, field.dataset.field);
    field.innerHTML = value ? nl2br(value) : '未入力';
  });
}

// ブラウザの入力補助設定
function setInputMode(form) {
  ZIP_FIELD_NAMES.forEach((name) => {
    getFields(form, name).forEach((field) => {
      field.setAttribute('inputmode', 'numeric');
      field.setAttribute('autocomplete', 'postal-code');
    });
  });

  TEL_FIELD_NAMES.forEach((name) => {
    getFields(form, name).forEach((field) => {
      field.setAttribute('inputmode', 'tel');
      field.setAttribute('autocomplete', 'tel');
    });
  });

  const autocompleteMap = {
    company: 'organization',
    full_name: 'name',
    staff_name: 'name',
    address: 'street-address',
    email: 'email',
  };

  Object.entries(autocompleteMap).forEach(([name, autocomplete]) => {
    getFields(form, name).forEach((field) => {
      field.setAttribute('autocomplete', autocomplete);
    });
  });
}

// 必須状態を JS 側でも明示して確認画面遷移前に弾けるようにする
function setRequiredState(form) {
  REQUIRED_FIELD_NAMES.forEach((name) => {
    const fields = getFields(form, name);

    if (fields.length === 0) {
      return;
    }

    fields.forEach((field, index) => {
      if (field.type === 'radio') {
        field.required = index === 0;
      } else {
        field.required = true;
      }
    });
  });

  // FAX は任意項目なので、CF7 の見た目クラスも外しておく
  getFields(form, 'fax').forEach((field) => {
    field.required = false;
    field.removeAttribute('aria-required');
    field.classList.remove('wpcf7-validates-as-required');
  });
}

// フリガナだけは独自ルールでチェックする
function validateKanaField(form) {
  const kanaField = form.querySelector('[name="kana"]');

  if (!kanaField) {
    return true;
  }

  const value = kanaField.value.trim();
  const isValid = value === '' || /^[ぁ-んァ-ヶー－ 　]+$/u.test(value);

  kanaField.setCustomValidity(isValid ? '' : 'ひらがなまたは全角カタカナで入力してください。');

  return isValid;
}

// 商品は数量1以上のものが最低1つ必要なので独自にチェックする
function validateProductField(form) {
  const productFields = Array.from(form.elements).filter(
    (element) => element.tagName === 'SELECT' && /^product\d+_qty$/.test(element.name),
  );

  if (productFields.length === 0) {
    return true;
  }

  const hasSelection = productFields.some((field) => Number.parseInt(field.value, 10) > 0);

  const [firstProduct] = productFields;
  firstProduct.setCustomValidity(hasSelection ? '' : '商品を1つ以上選択してください。');

  return hasSelection;
}

function validateBeforeConfirm(form) {
  setRequiredState(form);
  const isKanaValid = validateKanaField(form);
  const isProductValid = validateProductField(form);
  const isFormValid = form.reportValidity();

  return isKanaValid && isProductValid && isFormValid;
}

function isConfirmStepVisible(form) {
  return form.querySelector('[data-confirm]')?.style.display !== 'none';
}

// 現在の URL を基準に /thanks/ を組み立てる
function getThanksPageUrl() {
  const url = new URL(window.location.href);
  const basePath = url.pathname.endsWith('/') ? url.pathname : `${url.pathname}/`;

  url.pathname = `${basePath}thanks/`;
  url.search = '';
  url.hash = '';

  return url.toString();
}

function moveToThanksPage() {
  window.location.assign(getThanksPageUrl());
}

function handleConfirmClick(form) {
  if (!validateBeforeConfirm(form)) {
    return;
  }

  showConfirmStep(form);
  scrollToContactTop();
}

function handleBackClick(form) {
  showInputStep(form);
  scrollToContactTop();
}

// Enter などで form submit が走っても、入力画面では確認画面へ送る
function handleSubmit(form, event) {
  if (isConfirmStepVisible(form)) {
    return;
  }

  event.preventDefault();

  if (!validateBeforeConfirm(form)) {
    return;
  }

  showConfirmStep(form);
  scrollToContactTop();
}

// CF7 のイベントをまとめて管理する
function bindCf7Events(form) {
  form.addEventListener('wpcf7invalid', () => {
    showInputStep(form);
  });

  form.addEventListener('wpcf7mailfailed', () => {
    showInputStep(form);
  });

  form.addEventListener('wpcf7mailsent', () => {
    showCompleteStep(form);
    moveToThanksPage();
  });
}

function bindConfirmFlow(form) {
  const { confirmButton, backButton, defaultSubmit } = getFormParts(form);
  const kanaField = form.querySelector('[name="kana"]');

  if (!confirmButton) {
    return;
  }

  // 本来の submit ボタンは使わず、確認画面用のボタンから送信を制御する
  if (defaultSubmit) {
    defaultSubmit.style.display = 'none';
    defaultSubmit.tabIndex = -1;
    defaultSubmit.setAttribute('aria-hidden', 'true');
  }

  confirmButton.addEventListener('click', () => {
    handleConfirmClick(form);
  });

  backButton?.addEventListener('click', () => {
    handleBackClick(form);
  });

  kanaField?.addEventListener('input', () => {
    validateKanaField(form);
  });

  // 商品の数量を変更したら即座に必須エラーを解除できるようにする
  Array.from(form.elements)
    .filter((element) => element.tagName === 'SELECT' && /^product\d+_qty$/.test(element.name))
    .forEach((field) => {
      field.addEventListener('change', () => {
        validateProductField(form);
      });
    });

  form.addEventListener('submit', (event) => {
    handleSubmit(form, event);
  });

  bindCf7Events(form);
}

function contactForm() {
  const form = getContactForm();

  if (!form) {
    return;
  }

  setInputMode(form);
  setRequiredState(form);
  bindConfirmFlow(form);
  showInputStep(form);
  setupAddressAutofill(form);
}

// 住所自動入力(YubinBango)
// ==========================
function setupAddressAutofill(form) {
  const zipInput = form.querySelector('[name="zip"]');
  const addressInput = form.querySelector('[name="address"]');

  if (!zipInput || !addressInput) {
    return;
  }

  form.classList.add('h-adr');
  zipInput.classList.add('p-postal-code');
  addressInput.classList.add('p-region', 'p-locality', 'p-street-address', 'p-extended-address');

  if (!form.querySelector('.p-country-name')) {
    form.insertAdjacentHTML('afterbegin', '<input type="hidden" class="p-country-name" value="Japan">');
  }

  if (window.YubinBango && typeof window.YubinBango.MicroformatDom === 'function') {
    new window.YubinBango.MicroformatDom();
  }
}

export { contactForm };
